// Pure logic behind the run-history and run-state views
// (SM-538 / SM-546).
//
// Kept OUT of the components for one practical reason: this console's
// test runner is `node --experimental-strip-types`, which has no
// bundler alias table, so anything importing `@/components/...`
// cannot be imported by a test. A rule nobody can execute is a rule
// nobody is protecting — so the filter vocabulary, the status sets and
// the recovery grouping live here, with relative extensioned imports,
// and the components render them.

import type { MMRun, MMRunStateSection, RunStatus } from "./types.ts";

/**
 * Status GROUPS, not raw statuses.
 *
 * "Active" is a set (pending, running, pausing, paused, resuming,
 * stopping) because that is the question an operator actually asks.
 * A paused run must not fall out of "what is live" merely because
 * PAUSED became its own status.
 */
export const STATUS_GROUPS = {
  all: [] as RunStatus[],
  active: ["PENDING", "RUNNING", "PAUSING", "PAUSED", "RESUMING", "STOPPING"] as RunStatus[],
  paused: ["PAUSED"] as RunStatus[],
  stopped: ["STOPPED"] as RunStatus[],
  failed: ["FAILED"] as RunStatus[],
} as const;

export type StatusGroup = keyof typeof STATUS_GROUPS;

export function isStatusGroup(value: unknown): value is StatusGroup {
  return typeof value === "string" && value in STATUS_GROUPS;
}

/**
 * Serialize a group to the `statuses` query parameter.
 *
 * "all" serializes to undefined rather than to an empty string: an
 * empty `statuses=` would reach the server as a filter matching
 * nothing, which is the opposite of what "all" means.
 */
export function statusesParam(group: StatusGroup): string | undefined {
  const set = STATUS_GROUPS[group];
  return set.length > 0 ? set.join(",") : undefined;
}

export type ModeFilter = "all" | "CASH" | "GAME";

export function isModeFilter(value: unknown): value is ModeFilter {
  return value === "all" || value === "CASH" || value === "GAME";
}

export const RUN_STATE_SECTIONS: readonly MMRunStateSection[] = [
  "orders",
  "positions",
  "trades",
  "balances",
] as const;

export function isRunStateSection(value: unknown): value is MMRunStateSection {
  return RUN_STATE_SECTIONS.includes(value as MMRunStateSection);
}

export interface RunHistoryFilterState {
  status: StatusGroup;
  mode: ModeFilter;
  runID: string;
  from: string;
  to: string;
}

export const EMPTY_FILTERS: RunHistoryFilterState = {
  status: "all",
  mode: "all",
  runID: "",
  from: "",
  to: "",
};

export function hasActiveFilters(f: RunHistoryFilterState): boolean {
  return f.status !== "all" || f.mode !== "all" || !!f.runID || !!f.from || !!f.to;
}

/** One recovery chain: the original attempt plus everything linked. */
export interface AttemptGroup {
  rootRunID: string;
  attempts: MMRun[];
}

/**
 * Group runs by recovery root, preserving the incoming (newest-first)
 * order of the groups.
 *
 * Grouping by ROOT rather than by "has a predecessor" is what keeps a
 * three-attempt chain together even when the middle attempt is the
 * newest row on the page. A run with no recorded root is its own root,
 * which is what keeps every pre-lineage run visible.
 */
export function groupByRoot(runs: MMRun[]): AttemptGroup[] {
  const groups: AttemptGroup[] = [];
  const index = new Map<string, AttemptGroup>();
  for (const run of runs) {
    const root = run.recovery?.root_run_id || run.run_id;
    let group = index.get(root);
    if (!group) {
      group = { rootRunID: root, attempts: [] };
      index.set(root, group);
      groups.push(group);
    }
    group.attempts.push(run);
  }
  // Within a group, newest attempt first — the same direction as the
  // page, so the eye does not have to reverse mid-table.
  for (const group of groups) {
    group.attempts.sort((a, b) => (b.recovery?.attempt ?? 1) - (a.recovery?.attempt ?? 1));
  }
  return groups;
}

/**
 * How a run's duration reads.
 *
 * Returned as parts rather than a formatted string so the caller
 * supplies the localized unit words — a hard-coded "min" here would be
 * an English fragment the Thai locale could not reach.
 */
export function runDurationParts(
  run: Pick<MMRun, "start_at" | "start_requested_at" | "stop_at">,
  now = Date.now(),
): { hours: number; minutes: number } | null {
  const startRaw = run.start_at ?? run.start_requested_at;
  if (!startRaw) return null;
  const start = new Date(startRaw).getTime();
  const end = run.stop_at ? new Date(run.stop_at).getTime() : now;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  const totalMinutes = Math.round((end - start) / 60_000);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/**
 * Whether a run's state should be polled.
 *
 * A PAUSED run is active but frozen by design, so polling it would
 * churn the page without ever changing a row. This is the one place
 * that decides it, so run detail and preset detail cannot disagree.
 */
export function runStateIsLive(run: Pick<MMRun, "is_active" | "status"> | undefined): boolean {
  return !!run?.is_active && run.status !== "PAUSED";
}
